import type { components } from "../api/schema";
import { docUrl } from "../lib/docLinks";
import {
  Button,
  Checkbox,
  Code,
  Heading,
  HelpTip,
  Inline,
  InlineError,
  Skeleton,
  Stack,
  Text,
} from "../ui";

type ScopeCatalog = components["schemas"]["ApiKeyScopeCatalogResponse"];
type ScopeOperation = components["schemas"]["ApiKeyScopeCatalogOperation"];
type ScopePreset = components["schemas"]["ApiKeyScopeCatalogPreset"];

export interface ApiKeyScopeSelectorProps {
  catalog?: ScopeCatalog;
  errorMessage?: string;
  isPending: boolean;
  onChange: (scopes: string[]) => void;
  scopes: string[];
}

function concreteScopes(scopes: string[], presets: ScopePreset[]): Set<string> {
  const byToken = new Map(presets.map((preset) => [preset.token, preset]));
  return new Set(
    scopes.flatMap((scope) => byToken.get(scope)?.scopes ?? [scope]),
  );
}

function impliedScopes(
  concrete: Set<string>,
  operations: ScopeOperation[],
): Set<string> {
  return new Set(
    operations
      .filter((operation) => concrete.has(operation.token))
      .flatMap((operation) => operation.implied_scopes),
  );
}

function replaceScopeSet(
  current: Set<string>,
  scopes: string[],
  enabled: boolean,
): string[] {
  for (const scope of scopes) {
    if (enabled) {
      current.add(scope);
    } else {
      current.delete(scope);
    }
  }
  return [...current].sort();
}

export function ApiKeyScopeSelector({
  catalog,
  errorMessage,
  isPending,
  onChange,
  scopes,
}: ApiKeyScopeSelectorProps) {
  if (isPending) {
    return <Skeleton label="Loading grantable scopes" shape="detail-field" />;
  }
  if (errorMessage) {
    return <InlineError>{errorMessage}</InlineError>;
  }
  if (!catalog) {
    return null;
  }

  const operations = catalog.resources.flatMap(
    (resource) => resource.operations,
  );
  const selected = concreteScopes(scopes, catalog.presets);
  const implied = impliedScopes(selected, operations);
  const effective = new Set([...selected, ...implied]);
  const globalPresets = catalog.presets.filter((preset) =>
    preset.token.startsWith("*:"),
  );
  const resourcePresets = new Map(
    catalog.presets
      .filter((preset) => !preset.token.startsWith("*:"))
      .map((preset) => [preset.token, preset]),
  );

  function toggleScopes(target: string[], enabled: boolean): void {
    onChange(replaceScopeSet(new Set(selected), target, enabled));
  }

  function presetIsSelected(preset: ScopePreset): boolean {
    return preset.scopes.every((scope) => selected.has(scope));
  }

  function togglePreset(preset: ScopePreset, enabled: boolean): void {
    const remaining = replaceScopeSet(new Set(selected), preset.scopes, false);
    onChange(enabled ? [...remaining, preset.token].sort() : remaining);
  }

  return (
    <Stack className="marker-api-key-scopes" gap={4}>
      <div>
        <Inline align="center" gap={2}>
          <Heading level={2} size="sm">
            Permissions
          </Heading>
          {/* Immutability is verified, not assumed: `/v1/api-keys` exposes only
              POST / GET / DELETE (no PATCH, PUT, or rotate), the only UPDATE on
              `api_keys` sets `revoked_at`, and `scopes` is written exactly once
              by the INSERT in the Postgres api-key repository. Re-scoping
              really does mean a new key, and the row action is Revoke. */}
          <HelpTip
            docHref={docUrl("account/api-keys")}
            docLabel="API keys"
            label="About key scopes"
          >
            {"Grant the narrowest set of scopes the integration needs. " +
              "Scopes are fixed at creation; to change them, create a new " +
              "key and revoke this one."}
          </HelpTip>
        </Inline>
      </div>

      {globalPresets.length > 0 ? (
        <Stack gap={2}>
          <Text variant="caps">Shortcuts</Text>
          <Inline align="stretch" gap={2}>
            {globalPresets.map((preset) => {
              const active = presetIsSelected(preset);
              return (
                <Button
                  aria-pressed={active}
                  key={preset.token}
                  size="sm"
                  type="button"
                  variant={active ? "secondary" : "ghost"}
                  onClick={() => {
                    togglePreset(preset, !active);
                  }}
                >
                  {preset.label}
                </Button>
              );
            })}
          </Inline>
        </Stack>
      ) : null}

      <div className="marker-api-key-scopes__resources">
        {catalog.resources.map((resource) => {
          const preset = resourcePresets.get(`${resource.resource}:*`);
          const presetActive = preset ? presetIsSelected(preset) : false;
          return (
            <section
              className="marker-api-key-scopes__resource"
              key={resource.resource}
            >
              <div className="marker-api-key-scopes__resource-heading">
                <Inline align="center" gap={2}>
                  <Heading level={3} size="sm">
                    {resource.label}
                  </Heading>
                  <HelpTip
                    label={`About ${resource.label.toLowerCase()} access`}
                  >
                    {resource.description}
                  </HelpTip>
                </Inline>
                {preset ? (
                  <Button
                    aria-pressed={presetActive}
                    size="sm"
                    type="button"
                    variant={presetActive ? "secondary" : "ghost"}
                    onClick={() => {
                      togglePreset(preset, !presetActive);
                    }}
                  >
                    Full access
                  </Button>
                ) : null}
              </div>
              <div className="marker-api-key-scopes__operations">
                {resource.operations.map((operation) => {
                  const isImplied = implied.has(operation.token);
                  return (
                    <Checkbox
                      checked={effective.has(operation.token)}
                      className="marker-api-key-scopes__option"
                      disabled={isImplied}
                      key={operation.token}
                      label={
                        <span className="marker-api-key-scopes__option-title">
                          {operation.label} <Code>{operation.token}</Code>
                        </span>
                      }
                      onChange={(event) => {
                        toggleScopes(
                          [operation.token],
                          event.currentTarget.checked,
                        );
                      }}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {catalog.actions.length > 0 ? (
        <section className="marker-api-key-scopes__actions">
          <Heading level={3} size="sm">
            Actions
          </Heading>
          {catalog.actions.map((action) => (
            <Checkbox
              checked={selected.has(action.token)}
              className="marker-api-key-scopes__option"
              key={action.token}
              label={
                <span className="marker-api-key-scopes__option-title">
                  <Inline align="center" gap={2}>
                    {action.label} <Code>{action.token}</Code>
                    <HelpTip label={`About ${action.label.toLowerCase()}`}>
                      {action.description}
                    </HelpTip>
                  </Inline>
                </span>
              }
              onChange={(event) => {
                toggleScopes([action.token], event.currentTarget.checked);
              }}
            />
          ))}
        </section>
      ) : null}

      <Text variant="small">
        {selected.size === 0
          ? "No scopes selected. This key will have no API access."
          : `${String(selected.size)} scope${selected.size === 1 ? "" : "s"} selected.`}
      </Text>
    </Stack>
  );
}
